// src/pages/IndicesCommerceExterieur.jsx
import React, { useState, useEffect, useMemo } from 'react';
import NavigationBar from '../navbar/NavigationBar';
import { useNavigate } from 'react-router-dom';
import Footer from '../navbar/Footer';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import {
  Container,
  Typography,
  Grid,
  Card,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  styled,
  createTheme,
  ThemeProvider,
  Box,
  CardMedia,
  Button,
} from '@mui/material';
import moment from 'moment';
import { getPublications } from '../services/publicationService';

function IndicesCommerceExterieur() {
  const navigate = useNavigate();
  const [visibleCount, setVisibleCount] = useState(4);
  const [selectedYear, setSelectedYear] = useState('Tous');
  const [indicesData, setIndicesData] = useState([]);
  const [filteredIndices, setFilteredIndices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Récupération des données depuis l'API
  useEffect(() => {
    setLoading(true);
    getPublications({ type: 'ice', perPage: 1000 })
      .then((res) => {
        if (res.error) {
          setError(res.error);
        } else {
          // Mapper les données de l'API vers la structure attendue
          const mapped = res.data.map((item) => ({
            title: item.title,
            principalLink: item.document_url,
            image: item.image_url,
            description: item.description,
            date: item.date,
            publicationDate: item.publication_date,
          }));
          // Trier par date décroissante
          const sorted = sortIndicesByDate(mapped);
          setIndicesData(sorted);
          setFilteredIndices(sorted);
        }
      })
      .catch((err) => {
        setError('Erreur lors du chargement des données.');
        console.error(err);
      })
      .finally(() => setLoading(false));
  }, []);

  // Fonction de tri par date décroissante
  const sortIndicesByDate = (data) => {
    return data.sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  const handleShowMore = () => {
    setVisibleCount((prevCount) => prevCount + 2);
  };

  const handleYearChange = (event) => {
    const year = event.target.value;
    setSelectedYear(year);

    let filtered;
    if (year === 'Tous') {
      filtered = [...indicesData];
    } else {
      filtered = indicesData.filter(
        (indice) => new Date(indice.date).getFullYear() === parseInt(year)
      );
    }
    setFilteredIndices(sortIndicesByDate(filtered));
    setVisibleCount(2);
  };

  const StyledSelect = styled(Select)(({ theme }) => ({
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.light,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.dark,
    },
  }));

  const theme = createTheme({
    palette: {
      primary: { main: '#388E3C' },
      background: { default: '#f5f5f5' },
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      body1: {
        fontSize: 16,
        letterSpacing: 0.5,
        color: '#6B778C',
      },
      h4: {
        fontWeight: '600',
      },
    },
  });

  // Années disponibles à partir des données chargées
  const availableYears = useMemo(() => {
    const years = indicesData.map((indice) => new Date(indice.date).getFullYear());
    return [...new Set(years)].sort((a, b) => b - a);
  }, [indicesData]);

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <div style={{ backgroundColor: theme.palette.background.default, paddingBottom: '32px' }}>
          <NavigationBar />
          <br /><br />
          <Container maxWidth="lg" style={{ paddingTop: '14px' }}>
            <Typography variant="h4" align="justify" gutterBottom style={{ color: theme.palette.primary.main, marginBottom: '16px' }}>
              Indices du Commerce Extérieur (ICE)
            </Typography>
            <Divider style={{ margin: '16px 0' }} />
            <Typography variant="body1">Chargement des données...</Typography>
          </Container>
          <Footer />
        </div>
      </ThemeProvider>
    );
  }

  if (error) {
    return (
      <ThemeProvider theme={theme}>
        <div style={{ backgroundColor: theme.palette.background.default, paddingBottom: '32px' }}>
          <NavigationBar />
          <br /><br />
          <Container maxWidth="lg" style={{ paddingTop: '14px' }}>
            <Typography variant="h4" align="justify" gutterBottom style={{ color: theme.palette.primary.main, marginBottom: '16px' }}>
              Indices du Commerce Extérieur (ICE)
            </Typography>
            <Divider style={{ margin: '16px 0' }} />
            <Typography variant="body1" color="error">{error}</Typography>
          </Container>
          <Footer />
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <div style={{ backgroundColor: theme.palette.background.default, paddingBottom: '32px' }}>
        <NavigationBar />
        <br />
        <br />
        <Container maxWidth="lg" style={{ paddingTop: '14px' }}>
          <Typography variant="h4" align="justify" gutterBottom style={{ color: theme.palette.primary.main, marginBottom: '16px' }}>
            Indices du Commerce Extérieur (ICE)
          </Typography>
          <Divider style={{ margin: '16px 0' }} />
          <Typography variant="body1" align="justify" paragraph style={{ marginBottom: '24px' }}>
            Les indices du commerce extérieur sont des outils indispensables à l'analyse économique des importations et des exportations.
            Ils permettent d'obtenir une estimation sur l'évolution des prix et des volumes des biens importés ou exportés.
          </Typography>

          {/* Filtre par année */}
          <FormControl variant="outlined" style={{ marginLeft: '0px', width: '300px' }}>
            <InputLabel id="year-select-label">Sélectionnez par l'année de réalisation</InputLabel>
            <StyledSelect
              labelId="year-select-label"
              id="year-select"
              value={selectedYear}
              onChange={handleYearChange}
              label="Sélectionnez par l'année de réalisation"
            >
              <MenuItem value="Tous">Tous les indices</MenuItem>
              {availableYears.map((year) => (
                <MenuItem key={year} value={year.toString()}>
                  {year}
                </MenuItem>
              ))}
            </StyledSelect>
          </FormControl>

          {/* Affichage des Indices */}
          <Grid container spacing={1} style={{ marginTop: '5px' }}>
            {filteredIndices.slice(0, visibleCount).map((indice, index) => (
              <Grid item xs={12} sm={12} key={index}>
                <Card elevation={3} style={{ borderRadius: '12px', overflow: 'hidden' }}>
                  <Box sx={{ display: 'flex' }}>
                    <a
                      href={indice.principalLink}
                      style={{ textDecoration: 'none', color: 'inherit' }}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <CardMedia
                        component="img"
                        sx={{ width: 91, height: 140, m: 2 }}
                        image={indice.image}
                        alt={indice.title}
                      />
                    </a>
                    <Box sx={{ display: 'flex', flexDirection: 'column', pl: 2, pt: 2 }}>
                      <Typography variant="h6" component="div" style={{ fontWeight: 'bold', marginBottom: '10px', color: theme.palette.primary.main }}>
                        <a
                          href={indice.principalLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ textDecoration: 'none', color: 'inherit' }}
                        >
                          <DownloadIcon /> {indice.title}
                        </a>
                      </Typography>
                      <Typography variant="body2" component="div" style={{ color: '#555', marginBottom: '10px' }}>
                        {indice.description}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Publié en {moment(indice.publicationDate).format('MMMM YYYY')}
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>

          {visibleCount < filteredIndices.length && (
            <Button
              onClick={handleShowMore}
              style={{
                marginTop: '20px',
                backgroundColor: theme.palette.primary.main,
                color: 'white',
              }}
            >
              Plus d'Indices
            </Button>
          )}

          <Typography variant="body1" align="justify" paragraph style={{ marginTop: '24px', lineHeight: 1.6 }}>
            <strong>Note :</strong> Les Indices du Commerce Extérieur (ICE) sont publiés le <strong>15 de chaque mois</strong>, soit 15 jours après la clôture du mois précédent.
            <br /><br />
            Voici le calendrier des prochaines publications des indices :
            <ul style={{ listStyleType: 'circle', margin: '10px 0 10px 20px', padding: 0 }}>
              <li><strong>15 janvier</strong> - Publication des indices du mois de décembre</li>
              <li><strong>15 février</strong> - Publication des indices du mois de janvier</li>
              <li><strong>15 mars</strong> - Publication des indices du mois de février</li>
              <li><strong>15 avril</strong> - Publication des indices du mois de mars</li>
              <li><strong>15 mai</strong> - Publication des indices du mois d'avril</li>
              <li><strong>15 juin</strong> - Publication des indices du mois de mai</li>
              <li><strong>15 juillet</strong> - Publication des indices du mois de juin</li>
              <li><strong>15 août</strong> - Publication des indices du mois de juillet</li>
              <li><strong>15 septembre</strong> - Publication des indices du mois d'août</li>
              <li><strong>15 octobre</strong> - Publication des indices du mois de septembre</li>
              <li><strong>15 novembre</strong> - Publication des indices du mois d'octobre</li>
              <li><strong>15 décembre</strong> - Publication des indices du mois de novembre</li>
            </ul>
          </Typography>

          <Button
            onClick={() => navigate('/indice')}
            style={{ marginLeft: '20px', marginTop: '20px', backgroundColor: '#2E7D32', color: '#FFFFFF' }}
          >
            <ArrowBackIcon style={{ marginRight: '8px' }} />
            Retour à la page des indices
          </Button>
        </Container>
        <br />
        <br />
        <Footer />
      </div>
    </ThemeProvider>
  );
}

export default IndicesCommerceExterieur;